<script setup lang="ts">
/*
 * Primary base-location picker (create-company path).
 *
 * Embeds a "Primary base location" section in the create-company form: a Name +
 * Address pair, a lat/lng coordinate input, and a lightweight Leaflet map with a
 * clickable / draggable pin (OpenStreetMap tiles, no API key). It produces the
 * structured `BaseLocation` wire object that mirrors v1's `Map.vue` `submitMap`
 * body exactly (`{ Name, Status, Type, Location:{Lat,Long as strings},
 * DetailedAddress }`) — see `employers.ts > BaseLocation`.
 *
 * Leaflet is **dynamically imported** (`import('leaflet')` + its CSS) inside
 * `onMounted` so it lands in its own async chunk and never inflates the initial
 * bundle (the main chunk has a strict size-limit gate). The map is a progressive
 * enhancement: the Name / Address / coordinate inputs work even if the map chunk
 * never loads (e.g. in jsdom / happy-dom under test), so the field is fully
 * usable without the widget.
 *
 * `v-model` is the FE-canonical `BaseLocation | null`: `null` until both a Name
 * and a valid coordinate are present; a complete object otherwise. The parent
 * form sends it on COMPANY create only.
 */
import { computed, onBeforeUnmount, onMounted, ref, shallowRef, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { FormField, TextInput } from '@/ui';
import type { BaseLocation } from '@/api/services/employers';

const model = defineModel<BaseLocation | null>({ default: null });

const props = withDefaults(
  defineProps<{
    /** When true, the field renders its "required" affordance + error copy. */
    required?: boolean;
    /** Show the "name + coordinates required" error (driven by the parent submit). */
    showError?: boolean;
  }>(),
  { required: false, showError: false },
);

const { t } = useI18n();

// Phnom Penh — matches v1's default map centre.
const DEFAULT_CENTER: [number, number] = [11.56354324220945, 104.91146459675183];

const name = ref('');
const address = ref('');
// The coordinate input mirrors v1's "Lat, Long" single field.
const coords = ref('');
const lat = ref<number | null>(null);
const lng = ref<number | null>(null);

/** Parse a "lat, lng" string into a finite pair, or null when malformed. */
function parseCoords(raw: string): { lat: number; lng: number } | null {
  const parts = raw.split(',').map((p) => p.trim());
  if (parts.length !== 2) return null;
  const la = Number(parts[0]);
  const ln = Number(parts[1]);
  if (!Number.isFinite(la) || !Number.isFinite(ln)) return null;
  if (la < -90 || la > 90 || ln < -180 || ln > 180) return null;
  return { lat: la, lng: ln };
}

const hasValidCoords = computed(() => lat.value !== null && lng.value !== null);
const isComplete = computed(() => Boolean(name.value.trim()) && hasValidCoords.value);

const errorText = computed(() =>
  props.showError && !isComplete.value ? t('v2.organization.company-form.base-location.error') : '',
);

/** Recompute the `BaseLocation` model from the field state. */
function syncModel(): void {
  if (!isComplete.value) {
    model.value = null;
    return;
  }
  model.value = {
    Name: name.value.trim(),
    Status: 'Active',
    Type: 'Primary',
    Location: { Lat: String(lat.value), Long: String(lng.value) },
    DetailedAddress: address.value.trim() || undefined,
  };
}

watch([name, address, lat, lng], syncModel);

// --- Leaflet (lazy) -------------------------------------------------------
// `marker` / `map` hold the live Leaflet instances; typed loosely (`unknown`
// cast at the call sites) so the module type isn't pulled into the eager graph.
const mapEl = ref<HTMLDivElement | null>(null);
const map = shallowRef<import('leaflet').Map | null>(null);
const marker = shallowRef<import('leaflet').Marker | null>(null);
let leaflet: typeof import('leaflet') | null = null;

/** Place / move the pin and recentre the map on a coordinate. */
function placePin(la: number, ln: number, recenter = true): void {
  if (!leaflet || !map.value) return;
  if (marker.value) {
    marker.value.setLatLng([la, ln]);
  } else {
    marker.value = leaflet.marker([la, ln], { draggable: true }).addTo(map.value);
    marker.value.on('dragend', () => {
      const ll = marker.value?.getLatLng();
      if (ll) applyPin(ll.lat, ll.lng);
    });
  }
  if (recenter) map.value.setView([la, ln], Math.max(map.value.getZoom(), 14));
}

/** Commit a pin position to the lat/lng refs + the coordinate input. */
function applyPin(la: number, ln: number): void {
  lat.value = la;
  lng.value = ln;
  coords.value = `${la}, ${ln}`;
}

/** Re-parse the coordinate text input on blur (mirrors v1's onCoordinatesChange). */
function onCoordsBlur(): void {
  const parsed = parseCoords(coords.value);
  if (!parsed) {
    lat.value = null;
    lng.value = null;
    return;
  }
  applyPin(parsed.lat, parsed.lng);
  placePin(parsed.lat, parsed.lng);
}

onMounted(async () => {
  try {
    // Pull Leaflet + its CSS into a separate async chunk on first paint of the
    // field. The CSS is bundled from the package (CSP-safe — no external host).
    const [L] = await Promise.all([import('leaflet'), import('leaflet/dist/leaflet.css')]);
    leaflet =
      (L as { default?: typeof import('leaflet') }).default ?? (L as typeof import('leaflet'));
    if (!mapEl.value) return;
    map.value = leaflet.map(mapEl.value).setView(DEFAULT_CENTER, 12);
    leaflet
      .tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 19,
        attribution: '© OpenStreetMap contributors',
      })
      .addTo(map.value);
    map.value.on('click', (e: import('leaflet').LeafletMouseEvent) => {
      applyPin(e.latlng.lat, e.latlng.lng);
      placePin(e.latlng.lat, e.latlng.lng, false);
    });
    // If a coordinate was typed before the map finished loading, drop the pin.
    if (lat.value !== null && lng.value !== null) placePin(lat.value, lng.value);
  } catch {
    // Map failed to load (offline / blocked) — the text inputs still work.
    leaflet = null;
  }
});

onBeforeUnmount(() => {
  map.value?.remove();
  map.value = null;
  marker.value = null;
});
</script>

<template>
  <fieldset
    class="flex flex-col gap-4 rounded-xl border border-app-border p-4"
    data-testid="base-location-field"
  >
    <legend class="px-1 text-sm font-semibold text-app-fg">
      {{ t('v2.organization.company-form.base-location.legend') }}
    </legend>

    <FormField
      :label="t('v2.organization.company-form.base-location.name')"
      :required="required"
      :error="errorText"
    >
      <template #default="{ id, describedBy, invalid }">
        <TextInput
          :id="id"
          v-model="name"
          :aria-describedby="describedBy"
          :aria-invalid="invalid"
          :placeholder="t('v2.organization.company-form.base-location.name-placeholder')"
          data-testid="base-location-name"
        />
      </template>
    </FormField>

    <FormField :label="t('v2.organization.company-form.base-location.address')">
      <template #default="{ id }">
        <TextInput
          :id="id"
          v-model="address"
          :placeholder="t('v2.organization.company-form.base-location.address-placeholder')"
          data-testid="base-location-address"
        />
      </template>
    </FormField>

    <FormField
      :label="t('v2.organization.company-form.base-location.coordinates')"
      :hint="t('v2.organization.company-form.base-location.coordinates-hint')"
    >
      <template #default="{ id }">
        <TextInput
          :id="id"
          v-model="coords"
          placeholder="11.568413, 104.919474"
          data-testid="base-location-coords"
          @blur="onCoordsBlur"
        />
      </template>
    </FormField>

    <!-- Leaflet map mount point — lazily hydrated in onMounted. -->
    <div
      ref="mapEl"
      class="h-60 w-full overflow-hidden rounded-lg border border-app-border bg-slate-50"
      data-testid="base-location-map"
      role="application"
      :aria-label="t('v2.organization.company-form.base-location.map-label')"
    />
  </fieldset>
</template>
